/* eslint-disable array-callback-return */
import validator from 'validator';

function AddEditExerciseVali(exercises) {
  const errors = {};
  const validationMessages = {
    enterNameErrorMessage: 'Please enter exercise name.',
    selectMuscleGroupsErrorMessage: 'Please select at least one muscle group.',
    enterDurationErrorMessage: 'Please enter exercise duration.',
    invalidDurationErrorMessage: 'Exercise duration must be greater than 0.',
    selectVideoErrorMessage: 'Please upload exercise video.',
    selectImageErrorMessage: 'Please upload exercise image.',
    invalidVideoFormatErrorMessage: 'Please upload a valid video file (MP4, AVI, MOV, WEBM).',
    invalidImageFormatErrorMessage: 'Please upload a valid image file (JPG, JPEG, PNG, GIF).',
    videoSizeErrorMessage: 'Video file size should be less than 200MB.',
    imageSizeErrorMessage: 'Image file size should be less than 1MB.',
    exercisesRequiredErrorMessage: 'At least one exercise is required.'
  };

  // Check if exercises array exists and has at least one exercise
  if (!exercises || exercises.length === 0) {
    errors.exercises = validationMessages.exercisesRequiredErrorMessage;
    return { errors, isValid: false };
  }

  // Validate each exercise
  exercises.forEach((exercise, index) => {
    const exercisePrefix = `exercise_${index}`;

    // Exercise name validation
    if (!exercise.name || validator.isEmpty(exercise.name.trim())) {
      errors[`${exercisePrefix}_name`] = validationMessages.enterNameErrorMessage;
    }

    // Muscle groups validation
    if (!exercise.muscle_groups || exercise.muscle_groups.length === 0) {
      errors[`${exercisePrefix}_muscle_groups`] = validationMessages.selectMuscleGroupsErrorMessage;
    }

    // Exercise duration validation
    if (exercise.exercise_duration) {
      const duration = parseFloat(exercise.exercise_duration);
      if (isNaN(duration) || duration <= 0) {
        errors[`${exercisePrefix}_duration`] = validationMessages.invalidDurationErrorMessage;
      }
    }

    // Video validation - either video file or video URL should be present
    const hasVideoFile = exercise.video && exercise.video instanceof File;
    const hasVideoUrl = exercise.video_url && !validator.isEmpty(exercise.video_url);

    if (!hasVideoFile && !hasVideoUrl) {
      errors[`${exercisePrefix}_video`] = validationMessages.selectVideoErrorMessage;
    }

    // Video file format validation if video file exists
    if (hasVideoFile) {
      const videoFormats = /\.(mp4|avi|mov|webm)$/i;
      if (!videoFormats.test(exercise.video.name)) {
        errors[`${exercisePrefix}_video_format`] =
          validationMessages.invalidVideoFormatErrorMessage;
      }

      // Video file size validation (200MB = 200 * 1024 * 1024 bytes)
      const maxVideoSize = 200 * 1024 * 1024;
      if (exercise.video.size > maxVideoSize) {
        errors[`${exercisePrefix}_video_size`] = validationMessages.videoSizeErrorMessage;
      }
    }

    // Image validation - either image file or image URL should be present
    const hasImageFile = exercise.image && exercise.image instanceof File;
    const hasImageUrl = exercise.image_url && !validator.isEmpty(exercise.image_url);

    if (!hasImageFile && !hasImageUrl) {
      errors[`${exercisePrefix}_image`] = validationMessages.selectImageErrorMessage;
    }

    // Image file format validation if image file exists
    if (hasImageFile) {
      const imageFormats = /\.(jpg|jpeg|png|gif)$/i;
      if (!imageFormats.test(exercise.image.name)) {
        errors[`${exercisePrefix}_image_format`] =
          validationMessages.invalidImageFormatErrorMessage;
      }

      // Image file size validation (1MB = 1024 * 1024 bytes)
      const maxImageSize = 1024 * 1024;
      if (exercise.image.size > maxImageSize) {
        errors[`${exercisePrefix}_image_size`] = validationMessages.imageSizeErrorMessage;
      }
    }
  });

  return {
    errors,
    isValid: Object.keys(errors).length === 0
  };
}

// Additional validation function for single exercise
function validateSingleExercise(exercise, index = 0) {
  const exercises = [exercise];
  const result = AddEditExerciseVali(exercises);

  // Transform errors to remove the exercise index prefix for single exercise validation
  const transformedErrors = {};
  Object.keys(result.errors).forEach((key) => {
    const newKey = key.replace(`exercise_${index}_`, '');
    transformedErrors[newKey] = result.errors[key];
  });

  return {
    errors: transformedErrors,
    isValid: result.isValid
  };
}

// Validation function for workout exercises data formatting
function validateWorkoutExercises(exercises) {
  const errors = {};

  if (!exercises || !Array.isArray(exercises)) {
    errors.exercises = 'Exercises must be an array';
    return { errors, isValid: false };
  }

  exercises.forEach((exercise, index) => {
    // Validate exercise has required fields for API submission
    if (!exercise.id && !exercise.exercise_id) {
      errors[`exercise_${index}_id`] = 'Exercise ID is required';
    }

    // Validate metadata structure if provided
    if (exercise.metadata && exercise.metadata.sets) {
      if (!Array.isArray(exercise.metadata.sets)) {
        errors[`exercise_${index}_sets`] = 'Exercise sets must be an array';
      } else {
        exercise.metadata.sets.forEach((set, setIndex) => {
          if (typeof set.set_number !== 'number' || set.set_number <= 0) {
            errors[`exercise_${index}_set_${setIndex}_number`] =
              'Set number must be a positive number';
          }

          // At least one of reps, weight, or duration should be provided
          if (!set.reps && !set.weight && !set.duration) {
            errors[`exercise_${index}_set_${setIndex}_data`] =
              'Set must have reps, weight, or duration';
          }
        });
      }
    }
  });

  return {
    errors,
    isValid: Object.keys(errors).length === 0
  };
}

export default AddEditExerciseVali;
export { validateSingleExercise, validateWorkoutExercises };
